import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { DxDataGridComponent } from "devextreme-angular";
import { InsightOrderSearchModel } from "src/app/core/models/individual-model/individual-order-model/insight-order-search.model";
import { OrdersMapperService } from "../../shared/order-mapper.service";
import { OrderSearchView } from "../../shared/views/order-search-view";
import { StringHelperService } from "src/app/core/utils/string-helper.service";
import { PrintHeaderModel } from "src/app/shared/ui/export-button-group";
import { StatusReferenceType } from "src/app/shared/ui/forms/inputs/status-badge/status-reference.type";
import { OrderDeliveryStatusReferenceModel, OrderPaymentStatusReferenceModel, OrderSalesStatusReferenceModel } from "src/app/core/models/reference-model/reference-general-model";
import { BehaviorSubject, Observable } from "rxjs";
import { OrderDeliveryStatusReferenceService, OrderPaymentStatusReferenceService, OrderSalesStatusReferenceService } from "src/app/core/services/system-services";
import { NavigationService } from "src/app/shared/utils/navigation";
import { SecurityGroupSecurityModel } from "src/app/core/models/security-model/security-group-security.model";
import { DateConverterService } from "src/app/core/utils/date-converter.service";
import { PagingDataView } from "src/app/core/views/pagging-data.view";

@Component({
    selector: 'op-insight-sales-summary-result-order-list',
    templateUrl: './sales-summary-result-order-list.component.html',
    providers: [
        OrdersMapperService
    ]
})
export class InsightSalesSummaryResultOrderListComponent implements OnChanges {
    readonly DATE_WITH_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss"
    public readonly EXPORT_FILE_NAME = "Sales Summary Order List"
    private readonly ORDER_SECURITY_CODE = ['ORDER', 'ORDERALL', 'ORDERCATALOG'];

    public StatusReferenceType = StatusReferenceType;
    
    public showProductList = true;
    public exportData: any;
    public header: PrintHeaderModel[];
    public orderSearchResult: OrderSearchView[]
    public focused: boolean = false;
    public resultCount: number = 0;
    public pagingDataViews: PagingDataView[];
    
    public focusing: boolean = false;
    

    public pdfDefaultStyle = {
        fontSize: 10
    };

    @Input() individualId: string;
    @Input() insightOrderSearchResults: InsightOrderSearchModel[];
    @Input() actionSecurity: SecurityGroupSecurityModel;

    @Output() onChangeListView = new EventEmitter();

    @ViewChild('ordersSearchGrid', { static: false }) dataGrid: DxDataGridComponent;

    orderSalesStatusReferenceModel$ = new BehaviorSubject<OrderSalesStatusReferenceModel[]>(null);
    orderPaymentStatusReferenceModel$ = new BehaviorSubject<OrderPaymentStatusReferenceModel[]>(null);
    orderDeliveryStatusReferenceModel$ = new BehaviorSubject<OrderDeliveryStatusReferenceModel[]>(null);

    constructor(private changeDetector: ChangeDetectorRef,
        private mapperService: OrdersMapperService,
        private orderSalesStatusReferenceService: OrderSalesStatusReferenceService,
        private orderDeliveryStatusReferenceService: OrderDeliveryStatusReferenceService,
        private orderPaymentStatusReferenceService: OrderPaymentStatusReferenceService,
        private navigationService: NavigationService,
        private stringUtils: StringHelperService,
        private dateConverter: DateConverterService) {

    }

    ngAfterViewInit(): void {
        this.getDataGridHeader();
        this.loadOrderSalesStatusReference();
        this.loadOrderDeliveryStatusReference();
        this.loadOrderPaymentStatusReference();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['insightOrderSearchResults']) {
            this.orderSearchResult = this.mapperService.toOrderSearchViews(this.insightOrderSearchResults);
            this.createPagingDatas(this.orderSearchResult);
            this.changeDetector.detectChanges();
        }
    }

    private loadOrderSalesStatusReference() {
        this.orderSalesStatusReferenceService.getOrderSalesStatusReference()
            .subscribe(
                (responses: OrderSalesStatusReferenceModel[]) => {
                    this.orderSalesStatusReferenceModel$.next(responses);
                }
            )
    }

    private loadOrderDeliveryStatusReference() {
        this.orderDeliveryStatusReferenceService.getOrderDeliveryStatusReference()
            .subscribe(
                (responses: OrderDeliveryStatusReferenceModel[]) => {
                    this.orderDeliveryStatusReferenceModel$.next(responses);
                }
            )
    }

    private loadOrderPaymentStatusReference() {
        this.orderPaymentStatusReferenceService.getOrderPaymentStatusReference()
            .subscribe(
                (responses: OrderPaymentStatusReferenceModel[]) => {
                    this.orderPaymentStatusReferenceModel$.next(responses);
                }
            )
    }

    individualFullName = (view: OrderSearchView) => {
        if (view.groupName) {
            return view.groupName
        }
        return view.individualName
    }

    public fetchRowsData(e) {
        this.resultCount = e.component.totalCount();
        this.getExportData()?.done((filteredData) => {
            this.exportData = this.mapExportData(filteredData);
        });        
  
        this.changeDetector.detectChanges();
    }

    private getExportData() {
        let filterExpr = this.dataGrid?.instance?.getCombinedFilter();
        let gridDataSource = this.dataGrid?.instance?.getDataSource();
        return gridDataSource?.store().load({
            filter: filterExpr
        });
    }

    changeListView() {
        this.onChangeListView.emit();
    }

    public onCellPrepared(e) {
        if (e.rowType != 'data') {
            return;
        }
        if (e.column.dataField == "totalAmount" || e.column.dataField == "paidAmount" || e.column.dataField == "outstandingAmount") {
            e.cellElement.css("text-align", "right");
        }
    }

    public getAmountValue(amount: number) {
        if (amount === 0) {
            return '';
        }

        return this.stringUtils.validateNullAmountDecimal(amount);
    }

    public getColorAmountDisplay(cell): string {
        if (cell.column.dataField == 'outstandingAmount') {
            return this.getOutstandingBalanceColor(cell?.key?.outstandingAmount)
        } else if (cell.column.dataField == 'paidAmount') {
            return this.getPaidAmountColor(cell?.key?.paidAmount, cell?.key?.totalAmount);
        }
        return '';
    }

    public getOutstandingBalanceColor(outstandingBalance: number): string {
        if (outstandingBalance > 0) {
            return 'color-danger-500';
        } else if (outstandingBalance < 0) {
            return 'outstanding-amount-refund'
        }
        return '';
    }

    public getPaidAmountColor(paidAmount: number, totalAmount: number): string {
        if (paidAmount > 0) {
            return 'paid-amount-fully-paid'
        }
        return '';
    }

    public mapExportData(filteredData) {
        return filteredData.map(dat => (
        {
            orderNumber: dat.orderNumber,
            orderDate: dat.orderDate,
            orderNumberPoinOfSales: dat.orderNumberPOS,
            organisation: dat.organisationCallName,
            salesStatus: dat.orderSalesStatusName,
            deliveryStatus: dat.orderDeliveryStatusName,
            paymentStatus: dat.orderPaymentStatusName,
            currency: dat.currencyCode,
            totalAmount: dat.totalAmount,
            paidAmount: dat.paidAmount,
            outstandingAmount: dat.outstandingAmount,
        }
    ));
    }

    findHighlightColorForOrderSalesStatus(dataSet: OrderSalesStatusReferenceModel[], value: string): string {
        const code = dataSet.find(s => s.orderSalesStatusName == value)?.highlightColor
        return code
    }

    findHighlightColorForOrderDeliveryStatus(dataSet: OrderDeliveryStatusReferenceModel[], value: string): string {
        const code = dataSet.find(s => s.orderDeliveryStatusName == value)?.highlightColor
        return code
    }

    findHighlightColorForOrderPaymentStatus(dataSet: OrderPaymentStatusReferenceModel[], value: string): string {
        const code = dataSet.find(s => s.orderPaymentStatusName == value)?.highlightColor
        return code
    }

    private getDataGridHeader() {
        this.header = [
            {
                mapField: "orderNumber",
                displayName: "Order Number"
            },
            {
                mapField: "orderDate",
                displayName: "Order Date"
            },
            {
                mapField: "orderNumberPoinOfSales",
                displayName: "Order Number POS"
            },
            {
                mapField: "organisation",
                displayName: "Organisation"
            },
            {
                mapField: "salesStatus",
                displayName: "Sales Status"
            },
            {
                mapField: "deliveryStatus",
                displayName: "Delivery Status"
            },
            {
                mapField: "paymentStatus",
                displayName: "Payment Status"
            },
            {
                mapField: "currency",
                displayName: "Currency"
            },
            {
                mapField: "totalAmount",
                displayName: "Total Amount"
            },
            {
                mapField: "paidAmount",
                displayName: "Paid Amount"
            },
            {
                mapField: "outstandingAmount",
                displayName: "Outstanding Amount"
            },
        ];
    }

    public doubleClick(item) {
        if (!this.ORDER_SECURITY_CODE.includes(this.actionSecurity?.securityCode)) return;
        
        let params = {
            selectedData: item.key,
            searchCriteria: null,
            userSecurity: this.actionSecurity,
            pagingData: this.pagingDataViews,
            currentIndex: item.key.no,
            myOrders: false,
            activityId: item?.key?.orderId,
            isFromSaleSummary: true
        };

        this.navigationService.navigate("/main/orders/details", "Orders Management", true, params);
    }

    protected formatShortDate(date: Date | string): string {
        return this.dateConverter.formatSystemShortDate(date);
    }

    public createPagingDatas(views: OrderSearchView[]) {
            this.pagingDataViews = []
            let index = 1;
            views?.forEach(view => {
                this.addPagingData(index, view);
                index += 1;
            })
    }

    public addPagingData(index: number, view: OrderSearchView) {
        let pagingData = new PagingDataView();
        pagingData.no = index;
        pagingData.id = view.orderId;
        pagingData.name = this.createName(view);
        this.pagingDataViews.push(pagingData);
    }

    private createName(view: OrderSearchView): string {
        let individualName = view.individualName;
        if (view.groupName) individualName = view.groupName;

        return "Order " + view.orderNumber + "<br/>" + individualName;
    }
}

